import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Printer, Download, Loader2, ChevronDown, FileText, Receipt, Truck } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { Button } from '@/components/ui/Button';
import { Spinner, EmptyState } from '@/components/ui/EmptyState';
import { fetchFullInvoiceForOrderAdmin } from '@/lib/invoiceApi';
import { downloadInvoicePdf, printInvoicePdf, willFitOnOnePage } from '@/lib/downloadInvoicePdf';
import { waitForImages } from '@/lib/waitForImages';
import { InvoiceDocument, type InvoiceTemplateData, type InvoiceRequestData, type InvoicePayment } from '@/components/invoices/InvoiceDocument';
import type { InvoiceRefund } from '@/components/invoices/RefundWatermark';
import { ReceiptDocument } from '@/components/invoices/ReceiptDocument';
import { DeliveryChallanDocument, type DeliveryChallanTemplateData, type DeliveryChallanData } from '@/components/invoices/DeliveryChallanDocument';
import { rxkModuleIcon, rxkModuleTitle, rxkNavGroups } from '../../../nav';

type DocFormat = 'a4' | 'pos' | 'challan';

export default function AdminOrderInvoicePage() {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const autoPrintHandled = useRef(false);
  const [template, setTemplate] = useState<InvoiceTemplateData | null>(null);
  const [request, setRequest] = useState<InvoiceRequestData | null>(null);
  const [payment, setPayment] = useState<InvoicePayment | undefined>(undefined);
  const [salesPerson, setSalesPerson] = useState<string | null>(null);
  const [soldBy, setSoldBy] = useState<string | null>(null);
  const [refund, setRefund] = useState<InvoiceRefund | null>(null);
  const [cancelled, setCancelled] = useState(false);
  const [cancelReason, setCancelReason] = useState<string | null>(null);
  const [challanTemplate, setChallanTemplate] = useState<DeliveryChallanTemplateData | null>(null);
  const [receivedBy, setReceivedBy] = useState<{ contactPerson: string | null; designation: string | null }>({ contactPerson: null, designation: null });
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [docFormat, setDocFormat] = useState<DocFormat>('a4');
  const [pendingAction, setPendingAction] = useState<'print' | 'download' | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  // Challan remarks are typed fresh before every print (see
  // DeliveryChallanDocument.tsx) rather than stored.
  const [showChallanRemarks, setShowChallanRemarks] = useState(false);
  const [challanRemarks, setChallanRemarks] = useState('');
  const invoiceRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    if (!orderId) return;
    setLoading(true);
    const result = await fetchFullInvoiceForOrderAdmin(orderId);
    if (!result) {
      setLoading(false);
      return;
    }
    setTemplate(result.template);
    setRequest(result.request);
    setPayment(result.payment);
    setSalesPerson(result.salesPerson);
    setSoldBy(result.soldBy);
    setChallanTemplate(result.challanTemplate);
    setReceivedBy(result.receivedBy);
    setRefund(result.refund);
    setCancelled(result.cancelled);
    setCancelReason(result.cancelReason);
    setLoading(false);
  }, [orderId]);

  useEffect(() => {
    load();
  }, [load]);

  // Auto-print when arriving via the orders list's inline A4/POS picker
  // (?format=pos&print=1) — jump straight to print instead of landing here
  // and making the admin open the Print dropdown again. Fires once, after
  // the document has actually loaded, then strips the params so a refresh
  // doesn't reprint.
  useEffect(() => {
    if (loading || !request || !template || autoPrintHandled.current) return;
    if (searchParams.get('print') !== '1') return;
    autoPrintHandled.current = true;
    const format: DocFormat = searchParams.get('format') === 'pos' ? 'pos' : 'a4';
    setDocFormat(format);
    setPendingAction('print');
    setSearchParams({}, { replace: true });
  }, [loading, request, template, searchParams, setSearchParams]);

  // Overrides the app-wide A4 @page rule (src/index.css) only while the POS
  // format is selected, so a thermal receipt prints at whatever width the
  // loaded roll actually is instead of being forced onto A4. Height is an
  // explicit, generously tall 1000mm rather than "auto" — "auto" for both
  // dimensions left the real page length up to the printer/OS, which on a
  // real thermal printer forced a page break (and a blank 2nd "page")
  // partway down a receipt that should have printed continuously. An
  // explicit height this tall gives the browser no reason to ever
  // paginate a normal receipt, while the driver still only advances/cuts
  // the roll as far as the actual content goes.
  useEffect(() => {
    if (docFormat !== 'pos') return;
    const style = document.createElement('style');
    style.id = 'receipt-page-size-override';
    style.textContent = '@media print { @page { size: auto 1000mm; margin: 2mm; } }';
    document.head.appendChild(style);
    return () => {
      style.remove();
    };
  }, [docFormat]);

  // Runs print/download only after the docFormat state change has committed
  // and re-rendered the correct document — this is also what makes Download
  // safe to click while the POS preview is showing: it flips back to 'a4'
  // first and waits for that render before capturing, instead of PDF-ing
  // whatever happens to be on screen.
  useEffect(() => {
    if (!pendingAction) return;
    let cancelled = false;
    (async () => {
      if (pendingAction === 'print') {
        if (invoiceRef.current) await waitForImages(invoiceRef.current);
        if (cancelled) return;
        // A4 prints natively for the common single-page case (crisper
        // than any rasterized PDF) and only falls back to the same
        // pipeline Download uses when the invoice genuinely spans
        // multiple pages — native print's fixed footer can't paginate
        // correctly there. See willFitOnOnePage's own comment.
        // POS/Mushak/Challan are short, fixed-layout documents that
        // print correctly natively, so they're untouched either way.
        if (docFormat === 'a4' && invoiceRef.current && !willFitOnOnePage(invoiceRef.current)) {
          await printInvoicePdf(invoiceRef.current);
        } else {
          window.print();
        }
      } else if (invoiceRef.current && request) {
        setDownloading(true);
        await waitForImages(invoiceRef.current);
        if (!cancelled) await downloadInvoicePdf(invoiceRef.current, `${request.doc_number || 'invoice'}.pdf`);
        setDownloading(false);
      }
      if (!cancelled) setPendingAction(null);
    })();
    return () => {
      cancelled = true;
    };
  }, [docFormat, pendingAction, request]);

  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
        setShowChallanRemarks(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  const selectFormatAndPrint = (format: DocFormat) => {
    setMenuOpen(false);
    setShowChallanRemarks(false);
    setDocFormat(format);
    setPendingAction('print');
  };

  const handleDownload = useCallback(() => {
    setDocFormat('a4');
    setPendingAction('download');
  }, []);

  if (loading) {
    return (
      <AppShell moduleTitle={rxkModuleTitle} moduleIcon={rxkModuleIcon} navGroups={rxkNavGroups}>
        <Spinner />
      </AppShell>
    );
  }

  if (!template || !request) {
    return (
      <AppShell moduleTitle={rxkModuleTitle} moduleIcon={rxkModuleIcon} navGroups={rxkNavGroups}>
        <EmptyState title="Invoice not found" />
      </AppShell>
    );
  }

  // Same CHA- derivation as Mushak's Chalan No on erp-thekseoul — available
  // for any order, not gated on channel.
  const challanNumber = request.doc_number?.replace(/^INV-/, 'CHA-') ?? null;
  const challanRequest: DeliveryChallanData = {
    challan_number: challanNumber,
    issue_date: request.issue_date,
    company_name: request.company_name || request.recipient_name,
    contact_person: receivedBy.contactPerson,
    contact_designation: receivedBy.designation,
    mobile: request.company_contact,
    address: request.company_address,
    items: request.line_items.map((item) => ({
      description: item.description,
      unit: 'Pcs',
      quantity: item.quantity,
      remarks: challanRemarks,
    })),
  };

  return (
    <AppShell moduleTitle={rxkModuleTitle} moduleIcon={rxkModuleIcon} navGroups={rxkNavGroups}>
      <div className="space-y-4">
        <div className="flex items-center justify-between print:hidden">
          <button
            onClick={() => navigate(`/admin/orders/${orderId}`)}
            className="flex items-center gap-1.5 text-[12.5px] font-semibold text-ink-faint hover:text-ink cursor-pointer"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back to Order
          </button>
          <div className="flex items-center gap-2.5">
            <Button variant="secondary" onClick={handleDownload} disabled={downloading}>
              {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              Download PDF
            </Button>
            <div className="relative" ref={menuRef}>
              <Button onClick={() => setMenuOpen((o) => !o)}>
                <Printer className="w-4 h-4" /> Print
                <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${menuOpen ? 'rotate-180' : ''}`} />
              </Button>
              {menuOpen && !showChallanRemarks && (
                <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-2xl border border-border-subtle shadow-modal overflow-hidden z-50">
                  <button
                    onClick={() => selectFormatAndPrint('a4')}
                    className="w-full flex items-center gap-2.5 px-4 py-2.5 text-[12.5px] font-medium text-ink hover:bg-surface-muted cursor-pointer"
                  >
                    <FileText className="w-4 h-4 text-ink-faint" /> A4
                  </button>
                  <button
                    onClick={() => selectFormatAndPrint('pos')}
                    className="w-full flex items-center gap-2.5 px-4 py-2.5 text-[12.5px] font-medium text-ink hover:bg-surface-muted cursor-pointer border-t border-border-subtle"
                  >
                    <Receipt className="w-4 h-4 text-ink-faint" /> POS
                  </button>
                  <button
                    onClick={() => {
                      setChallanRemarks('');
                      setShowChallanRemarks(true);
                    }}
                    className="w-full flex items-center gap-2.5 px-4 py-2.5 text-[12.5px] font-medium text-ink hover:bg-surface-muted cursor-pointer border-t border-border-subtle"
                  >
                    <Truck className="w-4 h-4 text-ink-faint" /> Delivery Challan
                  </button>
                </div>
              )}
              {menuOpen && showChallanRemarks && (
                <div className="absolute right-0 top-full mt-2 w-72 bg-white rounded-2xl border border-border-subtle shadow-modal p-3.5 z-50 space-y-2.5">
                  <p className="text-[11.5px] font-semibold text-ink">Remarks (optional)</p>
                  <textarea
                    autoFocus
                    value={challanRemarks}
                    onChange={(e) => setChallanRemarks(e.target.value)}
                    placeholder="e.g. Full Box with Shopping bag"
                    rows={2}
                    className="w-full rounded-lg border border-border-subtle px-3 py-2 text-[12.5px] text-ink outline-none focus:border-primary-500"
                  />
                  <div className="flex items-center justify-end gap-2">
                    <button onClick={() => setShowChallanRemarks(false)} className="px-3 py-1.5 text-[12px] font-medium text-ink-faint hover:text-ink cursor-pointer">
                      Cancel
                    </button>
                    <Button onClick={() => selectFormatAndPrint('challan')}>Print Challan</Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* print:overflow-visible matters as much as the others here — this
            panel scrolls on screen (overflow-auto) to fit the preview area,
            and Chromium's print pipeline treats an overflow:auto/hidden
            ancestor as a real clip boundary during pagination, not just on
            screen. Without the override, only content within roughly this
            panel's own on-screen height printed at all — confirmed by
            generating an actual print PDF: a single Delivery Challan copy
            printed fine, but the second of two copies (which needed more
            vertical space than the panel's own scrollable viewport) had its
            signature block and footer silently clipped. */}
        <div className="rounded-2xl bg-[#EEF1F6] p-8 overflow-auto print:p-0 print:overflow-visible print:bg-white print:rounded-none">
          <div ref={invoiceRef} className="shadow-[0_8px_40px_-8px_rgba(27,42,74,0.35)] print:shadow-none mx-auto" style={{ width: 'fit-content' }}>
            {docFormat === 'pos' ? (
              <ReceiptDocument template={template} request={request} payment={payment} salesPerson={salesPerson} />
            ) : docFormat === 'challan' ? (
              challanTemplate && (
                <>
                  <DeliveryChallanDocument template={challanTemplate} request={challanRequest} copyLabel="customer" refund={refund} cancelled={cancelled} cancelReason={cancelReason} />
                  {/* Forces the Office Copy onto its own page — a plain
                      sibling div here is enough since break-after only
                      needs an in-flow box to act on. */}
                  <div style={{ breakAfter: 'page', pageBreakAfter: 'always' }} />
                  <DeliveryChallanDocument template={challanTemplate} request={challanRequest} copyLabel="office" refund={refund} cancelled={cancelled} cancelReason={cancelReason} />
                </>
              )
            ) : (
              <InvoiceDocument template={template} request={request} payment={payment} salesPerson={salesPerson} soldBy={soldBy} refund={refund} cancelled={cancelled} cancelReason={cancelReason} />
            )}
          </div>
        </div>
      </div>
    </AppShell>
  );
}
